/**
 * パスワードの再設定(承認者が行う)。2026-09-03 改修。
 *
 * パスワードは個人ごとに設定する。Cloud Functions(Admin SDK)を導入するまでは、
 * 「いまのパスワード」を使って入り直す方式でしか変更できない。
 * 承認者は控え(user_secrets)からいまのパスワードを確認できるので、
 * その値を初期値として入れておく。
 *
 * ※Blaze プランに変更して Cloud Functions を入れたら、いまのパスワード不要で
 *   再発行できるようになる(adminSetPassword)。
 */
import { useEffect, useState } from 'react';
import Modal from '@core/components/Modal';
import { useToast } from '@core/components/Toast';
import { useAuth } from '@core/auth/AuthProvider';
import { passwordErrorMessage, resetUserPassword } from './resetUserPassword';
import { fetchUserSecrets, recordPassword } from './secretsRepo';
import { normalizePassword } from '@core/lib/password';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import type { User } from '@core/lib/types';

/** 覚えやすく推測されにくい初期パスワードを作る */
function suggestPassword(): string {
  const words = ['Sakura', 'Fuji', 'Kaede', 'Hinata', 'Aoba', 'Mikan', 'Suzu', 'Tsubaki'];
  const w = words[Math.floor(Math.random() * words.length)];
  return `${w}${Math.floor(1000 + Math.random() * 9000)}`;
}

export default function PasswordResetModal({
  target,
  onDone,
  onClose,
}: {
  target: User;
  onDone: () => void;
  onClose: () => void;
}) {
  const toast = useToast();
  const { user: me } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState(suggestPassword());
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  // 控えがあれば「いまのパスワード」に入れておく(承認者だけが読める)
  useEffect(() => {
    let alive = true;
    void fetchUserSecrets()
      .then((all) => {
        const saved = all[target.id]?.password;
        if (alive && saved) setCurrent(saved);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [target.id]);

  async function submit() {
    if (next.length < 8) {
      toast('新しいパスワードは8文字以上にしてください');
      return;
    }
    setBusy(true);
    try {
      await resetUserPassword(target.email, current, next);
      await recordPassword(target.id, next, 'admin', me?.display_name);
      // 承認者が発行したパスワードなので、次のログインで本人に決め直してもらう(2026-09-08)
      await updateDoc(doc(getDb(), 'users', target.id), { password_set_at: null, updated_at: serverTimestamp() }).catch(() => undefined);
      setDone(true);
      onDone();
      toast(`${target.display_name} さんのパスワードを変更しました`);
    } catch (e) {
      toast(passwordErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={() => (busy ? undefined : onClose())} width="min(520px, 94vw)" showClose>
      <h3 style={{ marginBottom: 4 }}>パスワードを再設定 — {target.display_name}</h3>
      <p className="hint" style={{ marginBottom: 14 }}>
        新しいパスワードを決めて、本人に伝えてください。設定した値は「ユーザー」の一覧で確認できます。
      </p>

      {done ? (
        <>
          <div className="note">
            新しいパスワード: <b style={{ fontFamily: 'monospace', fontSize: 16 }}>{next}</b>
            <br />
            本人に伝えてください(編集画面の「本人に送信」からメールの下書きを作れます)。
          </div>
          <div className="modal-actions">
            <button className="btn-green" onClick={onClose}>
              閉じる
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
            <div>
              <label>いまのパスワード</label>
              <input type="text" autoComplete="off" value={current} onChange={(e) => setCurrent(normalizePassword(e.target.value))} />
              <p className="hint">控えがある場合は自動で入ります。分からないときは本人に確認してください。</p>
            </div>
            <div>
              <label>新しいパスワード(8文字以上)</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="text" autoComplete="off" value={next} onChange={(e) => setNext(normalizePassword(e.target.value))} />
                <button className="btn-line" style={{ whiteSpace: 'nowrap' }} onClick={() => setNext(suggestPassword())}>
                  作り直す
                </button>
              </div>
            </div>
          </div>
          <div className="modal-actions">
            <button className="btn-green" onClick={() => void submit()} disabled={busy}>
              {busy ? '変更中…' : '変更する'}
            </button>
            <button className="btn-line" onClick={onClose} disabled={busy}>
              キャンセル
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}
